import React, { useEffect, useState } from "react";
import { X, ArrowLeft, ArrowRight, Check } from "lucide-react";
import {
  dismissGuidePermanent,
  dismissGuideSession,
} from "@/lib/guides";
import { useI18n } from "@/lib/i18n";

/**
 * Reusable modal walkthrough used by FamFeel's quick guides.
 *
 * Props:
 *   guide  — { id, title, steps: [{ eyebrow, title, body }] }
 *   open   — boolean (controlled)
 *   onClose — called on any dismiss (session or permanent)
 *
 * Dismissal semantics:
 *   · “Got it” on the last step → permanent (localStorage)
 *   · “Skip” (anywhere) or closing via X/backdrop → session-only (sessionStorage)
 */
export default function QuickGuideDialog({ guide, open, onClose }) {
  const { t } = useI18n();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (open) setIdx(0);
  }, [open, guide?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // Escape only dismisses optional guides. Mandatory guides
      // (welcome tour) absorb Escape so a quick tap doesn't skip
      // onboarding by accident.
      if (e.key === "Escape" && !guide?.mandatory) handleSkip();
      if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, total - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, guide?.id]);

  if (!open || !guide) return null;
  const steps = guide.steps || [];
  const total = steps.length;
  const step = steps[idx] || { title: "", body: "" };
  const isLast = idx === total - 1;
  const mandatory = !!guide.mandatory;

  // Backdrop + close-button handler. On mandatory guides, taps
  // outside the card and the X button both no-op — Done on the last
  // step is the only exit. Optional guides keep the convenience.
  const handleSkip = () => {
    if (mandatory) return;
    dismissGuideSession(guide.id);
    onClose && onClose();
  };

  const handleDone = () => {
    dismissGuidePermanent(guide.id);
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/55 flex items-center justify-center p-4 md:p-6"
      onClick={handleSkip}
      data-testid={`guide-backdrop-${guide.id}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`guide-title-${guide.id}`}
    >
      <div
        className="bg-[color:var(--surface)] rounded-[20px] w-full max-w-lg border border-[color:var(--line)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`guide-dialog-${guide.id}`}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-3 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="label-tiny text-[color:var(--ink-3)]">
              {t(step.eyebrow) || t(guide.title)}
            </div>
            <h2
              id={`guide-title-${guide.id}`}
              className="font-display-italic text-2xl md:text-3xl leading-tight tracking-tight mt-1"
              data-testid={`guide-step-title-${guide.id}`}
            >
              {t(step.title)}
            </h2>
          </div>
          {!mandatory && (
            <button
              onClick={handleSkip}
              className="h-9 w-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center flex-shrink-0"
              data-testid={`guide-close-${guide.id}`}
              aria-label={t({ en: "Skip guide", da: "Spring guide over" })}
              title={t({ en: "Skip", da: "Spring over" })}
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Body */}
        <div
          className="px-6 pb-5 text-[15px] leading-relaxed text-[color:var(--ink-2)] whitespace-pre-line"
          data-testid={`guide-step-body-${guide.id}`}
        >
          {t(step.body)}
        </div>

        {/* Step dots */}
        <div
          className="px-6 pb-4 flex items-center gap-1.5"
          aria-hidden="true"
        >
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === idx
                  ? "w-6 bg-[color:var(--ink)]"
                  : i < idx
                    ? "w-2 bg-[color:var(--ink)]/40"
                    : "w-2 bg-[color:var(--line)]"
              }`}
            />
          ))}
          <span className="label-tiny text-[color:var(--ink-3)] ml-2">
            {idx + 1} / {total}
          </span>
        </div>

        {/* Footer / nav. Mandatory guides (welcome tour) hide the
            "Skip for now" link — onboarding has to be completed at
            least once so families understand the PIN model before
            sharing the join code. Optional walk-throughs keep it. */}
        <div className="px-6 pb-6 pt-2 flex items-center justify-between gap-3 border-t border-[color:var(--line-soft)]">
          {guide.mandatory ? (
            <span aria-hidden="true" />
          ) : (
            <button
              onClick={handleSkip}
              className="label-tiny underline text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
              data-testid={`guide-skip-${guide.id}`}
            >
              {t({ en: "Skip for now", da: "Spring over for nu" })}
            </button>
          )}
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button
                onClick={() => setIdx((i) => Math.max(i - 1, 0))}
                className="ff-btn inline-flex items-center gap-1"
                data-testid={`guide-back-${guide.id}`}
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={2} /> {t({ en: "Back", da: "Tilbage" })}
              </button>
            )}
            {!isLast ? (
              <button
                onClick={() => setIdx((i) => Math.min(i + 1, total - 1))}
                className="ff-btn ff-btn-primary inline-flex items-center gap-1"
                data-testid={`guide-next-${guide.id}`}
              >
                {t({ en: "Next", da: "Næste" })} <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            ) : (
              <button
                onClick={handleDone}
                className="ff-btn ff-btn-primary inline-flex items-center gap-1"
                data-testid={`guide-done-${guide.id}`}
              >
                <Check className="w-4 h-4" strokeWidth={2} /> {t({ en: "Got it", da: "Forstået" })}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
